import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Assignment,
  People,
  TrendingUp,
  AttachMoney,
  Add,
  Visibility,
  Business,
  CheckCircle
} from '@mui/icons-material';
import { useResponsive } from '../../hooks/useResponsive';
import { useAuth } from '../../contexts/AuthContext';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend }) => {
  // const { isMobile } = useResponsive();

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ backgroundColor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp 
                sx={{ 
                  fontSize: 16, 
                  color: trend.isPositive ? 'success.main' : 'error.main',
                  transform: trend.isPositive ? 'none' : 'rotate(180deg)'
                }} 
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: trend.isPositive ? 'success.main' : 'error.main',
                  fontWeight: 'bold'
                }}
              >
                {trend.isPositive ? '+' : ''}{trend.value}%
              </Typography>
            </Box>
          )}
        </Box>
        
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {value}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const BusinessDashboard: React.FC = () => {
  // const { isMobile } = useResponsive();
  const { state } = useAuth();

  // const manager = state.user?.manager;

  // Моковые данные для демонстрации
  const stats = [
    {
      title: 'Активные задачи',
      value: 12,
      icon: <Assignment />,
      color: '#2196f3',
      trend: { value: 15, isPositive: true }
    },
    {
      title: 'Исполнители',
      value: 28,
      icon: <People />,
      color: '#4caf50',
      trend: { value: 8, isPositive: true }
    },
    {
      title: 'Потрачено в месяце',
      value: '₽84,500',
      icon: <AttachMoney />,
      color: '#ff9800',
      trend: { value: 12, isPositive: false }
    },
    {
      title: 'Завершенные задачи',
      value: 156,
      icon: <CheckCircle />,
      color: '#9c27b0',
      trend: { value: 23, isPositive: true }
    }
  ];

  const recentTasks = [
    {
      id: 1,
      title: 'Создать дизайн логотипа',
      executor: 'Анна М.',
      status: 'В работе',
      deadline: '2024-01-25',
      budget: '₽3,000',
      applications: 12
    },
    {
      id: 2,
      title: 'Написать статью о продукте',
      executor: 'Игорь К.',
      status: 'На проверке',
      deadline: '2024-01-23',
      budget: '₽5,500',
      applications: 8
    },
    {
      id: 3,
      title: 'Сделать презентацию',
      executor: '-',
      status: 'Поиск исполнителя',
      deadline: '2024-01-30',
      budget: '₽2,200',
      applications: 5
    },
    {
      id: 4,
      title: 'Разработать бриф',
      executor: 'Мария С.',
      status: 'Завершена',
      deadline: '2024-01-20',
      budget: '₽4,000',
      applications: 15
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'В работе': return 'primary';
      case 'На проверке': return 'warning';
      case 'Поиск исполнителя': return 'secondary';
      case 'Завершена': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Панель управления бизнеса 📊
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Управляйте задачами и отслеживайте прогресс исполнителей
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Активные задачи */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Мои задачи
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  size="small"
                >
                  Создать задачу
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Задача</TableCell>
                      <TableCell>Исполнитель</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Дедлайн</TableCell>
                      <TableCell>Бюджет</TableCell>
                      <TableCell>Заявки</TableCell>
                      <TableCell>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTasks.map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                            {task.title}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {task.executor !== '-' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                                {task.executor.charAt(0)}
                              </Avatar>
                              {task.executor}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Не назначен
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={task.status} 
                            size="small" 
                            color={getStatusColor(task.status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(task.deadline).toLocaleDateString('ru-RU')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {task.budget}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {task.applications}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            variant="outlined"
                          >
                            Открыть
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Боковая панель */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Быстрые действия */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Быстрые действия
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    startIcon={<Add />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Создать задачу
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<People />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Найти исполнителей
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Business />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Настройки организации
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<AttachMoney />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    История платежей
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Топ исполнители */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Лучшие исполнители
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { name: 'Анна М.', rating: 4.9, tasks: 12 },
                    { name: 'Игорь К.', rating: 4.8, tasks: 8 },
                    { name: 'Мария С.', rating: 4.7, tasks: 15 }
                  ].map((executor, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {executor.name.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {executor.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ⭐ {executor.rating} • {executor.tasks} задач
                        </Typography>
                      </Box>
                      <Button size="small" variant="outlined">
                        Связаться
                      </Button>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Аналитика */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Аналитика за месяц
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Задач создано:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      24
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Задач завершено:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      18
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Ср. время выполнения:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      3.2 дня
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Ср. рейтинг работ:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      4.6 ⭐
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
